import { useEffect, useState } from 'react';
import { sessionScopeKey, useSessionScope } from '@/hooks/useSessionScope';

/** Refetches update clean forms; a dirty form keeps its captured CAS version until explicit Revert. */
export function useSettingsDraft<T>(
  latest: { form: T; version: number } | null,
  externalDirty = false,
) {
  const { identity } = useSessionScope();
  const scope = JSON.stringify(sessionScopeKey(identity));
  const [state, setState] = useState<{
    scope: string;
    form: T;
    baseline: T;
    version: number;
  } | null>(null);
  const current = state?.scope === scope ? state : null;
  const dirty =
    current !== null &&
    (externalDirty || JSON.stringify(current.form) !== JSON.stringify(current.baseline));
  useEffect(() => {
    setState((previous) => {
      if (!latest) return previous?.scope === scope ? previous : null;
      if (
        previous?.scope === scope &&
        (externalDirty || JSON.stringify(previous.form) !== JSON.stringify(previous.baseline))
      )
        return previous;
      if (previous?.scope === scope && previous.version > latest.version) return previous;
      if (
        previous?.scope === scope &&
        previous.version === latest.version &&
        JSON.stringify(previous.baseline) === JSON.stringify(latest.form)
      )
        return previous;
      return { scope, form: latest.form, baseline: latest.form, version: latest.version };
    });
  }, [externalDirty, latest, scope]);
  const accept = (value: { form: T; version: number } | null = latest) => {
    setState((previous) => {
      if (previous?.scope === scope && (!value || previous.version > value.version)) {
        return { ...previous, form: previous.baseline };
      }
      return value
        ? { scope, form: value.form, baseline: value.form, version: value.version }
        : null;
    });
  };
  return {
    scope,
    form: current?.form ?? null,
    baseline: current?.baseline ?? null,
    version: current?.version ?? null,
    dirty,
    changedOnServer: current !== null && latest !== null && current.version < latest.version,
    change: (form: T) =>
      setState((previous) => (previous?.scope === scope ? { ...previous, form } : previous)),
    accept,
  };
}

export function changedSettingsFields<T extends object>(
  form: T,
  baseline: T,
  keys: readonly (keyof T)[],
): Partial<T> {
  return Object.fromEntries(
    keys.filter((key) => form[key] !== baseline[key]).map((key) => [key, form[key]]),
  ) as Partial<T>;
}
